import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Check, Sparkles, Globe, Bookmark, Zap } from "lucide-react";

const TRIGGERS = {
  daily_limit: {
    icon: Zap,
    title: "You're out of free scans for today",
    sub: "Upgrade to keep generating names without waiting for the daily reset.",
  },
  com_locked: {
    icon: Globe,
    title: "Unlock .com domain search",
    sub: "See which .com domains are available and their estimated value.",
  },
  saved_limit: {
    icon: Bookmark,
    title: "You've hit your saved names limit",
    sub: "Upgrade to build a bigger shortlist of your favourite names.",
  },
  export_locked: { icon: Sparkles, title: "Exports are a Pro feature", sub: "Upgrade to export your shortlists as CSV." },
  bulk_locked: { icon: Sparkles, title: "Bulk domain checks are a Pro feature", sub: "Check hundreds of domains at once." },
  generic: { icon: Sparkles, title: "Do more with Namable", sub: "Upgrade for more scans, .com search and estimated domain values." },
};

const PERKS = [
  "More scans every month",
  ".com search unlocked",
  "AI estimated domain values",
  "Bigger saved shortlist",
  "CSV export",
];

export function Paywall({ open, trigger = "generic", onClose }) {
  const navigate = useNavigate();
  const { track } = useAuth();
  const t = TRIGGERS[trigger] || TRIGGERS.generic;
  const Icon = t.icon;

  useEffect(() => {
    if (open) track("paywall_displayed", { trigger });
  }, [open, trigger, track]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-[440px] rounded-xl p-0 overflow-hidden gap-0" data-testid="paywall-dialog">
        <DialogTitle className="sr-only">{t.title}</DialogTitle>
        <div className="brand-gradient px-6 pt-7 pb-6 text-white">
          <div className="w-11 h-11 rounded-[12px] bg-white/20 flex items-center justify-center mb-3">
            <Icon size={22} />
          </div>
          <h2 className="text-[22px] font-extrabold tracking-[-0.01em]">{t.title}</h2>
          <p className="text-[13px] text-white/85 mt-1.5 leading-relaxed">{t.sub}</p>
        </div>
        <div className="p-6">
          <ul className="space-y-2.5 mb-5">
            {PERKS.map((p) => (
              <li key={p} className="flex items-center gap-2.5 text-[14px] text-ink">
                <span className="w-5 h-5 rounded-full bg-success-bg flex items-center justify-center shrink-0">
                  <Check size={13} className="text-success" strokeWidth={3} />
                </span>
                {p}
              </li>
            ))}
          </ul>
          <Button
            className="w-full h-12 rounded-lg bg-brand hover:bg-brand-dark text-white font-bold btn-glow"
            data-testid="paywall-upgrade-btn"
            onClick={() => {
              track("plan_selected", { trigger });
              onClose?.();
              navigate("/pricing");
            }}
          >
            See plans & upgrade
          </Button>
          <button
            onClick={onClose}
            className="w-full mt-2 py-2 text-[13px] text-ink-subtle hover:text-ink-muted transition-colors"
            data-testid="paywall-dismiss-btn"
          >
            Maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
